import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useCoffee } from "@/context/CoffeeContext";
import { CoffeeModal } from "@/components/CoffeeModal";
import { outlineToMindmap } from "@/lib/mindmapConvert";
import { BTN_PRIMARY, BTN_GHOST, ICON_BTN, INPUT } from "@/lib/styles";
import { Sparkles, X, Network, FileText, Loader2, Coffee } from "lucide-react";
import { toast } from "sonner";

const EXAMPLES = [
  "Organizar mi boda para diciembre con presupuesto limitado",
  "Plan para lanzar mi tienda de ropa online",
  "Estudiar para el examen de historia la próxima semana",
];

export function AiGenerateModal({ open, folderId = "", onClose }) {
  const navigate = useNavigate();
  const { refresh } = useCoffee();
  const [prompt, setPrompt] = useState("");
  const [mode, setMode] = useState("mindmap");
  const [loading, setLoading] = useState(false);
  const [quota, setQuota] = useState(null);
  const [gated, setGated] = useState(false);
  const [coffeeOpen, setCoffeeOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setGated(false);
    api.get("/ai/quota")
      .then(({ data }) => setQuota(data))
      .catch((err) => { if (err.response?.status === 402) setGated(true); });
  }, [open]);

  if (!open) return null;

  const generate = async () => {
    if (prompt.trim().length < 3) { toast.info("Cuéntame un poco más de tu idea"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/ai/generate", { prompt: prompt.trim(), mode });
      let payload;
      if (mode === "mindmap") {
        payload = { type: "mindmap", title: data.title, mindmap: outlineToMindmap(data.title, data.outline), folder_id: folderId || null };
      } else {
        payload = { type: "text", title: data.title, content: `# ${data.title}\n\n${data.outline}`, folder_id: folderId || null };
      }
      const { data: note } = await api.post("/notes", payload);
      toast.success(`¡Listo! Te quedan ${data.remaining_ml} ml de café este mes`);
      onClose?.();
      navigate(mode === "mindmap" ? `/mindmap/${note.id}` : `/note/${note.id}`);
    } catch (err) {
      const code = err.response?.status;
      if (code === 402) { setGated(true); }
      else if (code === 429) { toast.error(err.response.data.detail); }
      else toast.error(formatApiError(err.response?.data?.detail) || "No se pudo generar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !loading && onClose?.()} data-testid="ai-modal">
      <div className="w-full max-w-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-heading font-medium text-xl flex items-center gap-2">
            <Sparkles size={20} className="text-amber-500" /> Crear con IA
          </h3>
          <button onClick={() => onClose?.()} className={ICON_BTN} data-testid="ai-close"><X size={16} /></button>
        </div>

        {gated ? (
          <div className="py-6 text-center">
            <div className="w-14 h-14 rounded-2xl bg-amber-500/10 text-amber-500 flex items-center justify-center mx-auto mb-4"><Coffee size={26} /></div>
            <p className="font-body text-sm text-slate-600 dark:text-slate-300 mb-5">Crear con IA necesita <b>café activo</b> ☕. Recarga una taza y el Escriba organizará tus ideas por ti.</p>
            <button onClick={() => setCoffeeOpen(true)} className={BTN_PRIMARY} data-testid="ai-recharge"><Coffee size={16} /> Recargar café</button>
          </div>
        ) : (
          <>
            <p className="font-body text-sm text-slate-500 dark:text-slate-400 mb-4">
              Describe tu idea o problema en tus palabras y el Escriba lo convierte en un {mode === "mindmap" ? "mapa mental" : "plan"} listo para usar.
            </p>
            <div className="flex gap-2 mb-3">
              <button onClick={() => setMode("mindmap")} className={`flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium border transition-colors ${mode === "mindmap" ? "bg-indigo-600 text-white border-indigo-600" : "border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300"}`} data-testid="ai-mode-mindmap">
                <Network size={16} /> Mapa mental
              </button>
              <button onClick={() => setMode("note")} className={`flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium border transition-colors ${mode === "note" ? "bg-indigo-600 text-white border-indigo-600" : "border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300"}`} data-testid="ai-mode-note">
                <FileText size={16} /> Nota / plan
              </button>
            </div>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              className={`${INPUT} h-28 resize-none`}
              placeholder="Ej: Quiero organizar mi boda para diciembre con presupuesto limitado..."
              data-testid="ai-prompt"
            />
            <div className="flex flex-wrap gap-2 mt-3">
              {EXAMPLES.map((ex) => (
                <button key={ex} onClick={() => setPrompt(ex)} className="text-[11px] px-2.5 py-1 rounded-full border border-slate-200 dark:border-slate-700 text-slate-500 hover:border-indigo-400 hover:text-indigo-500 transition-colors" data-testid="ai-example">
                  {ex}
                </button>
              ))}
            </div>
            <div className="flex items-center justify-between mt-5">
              <span className="font-mono2 text-[11px] text-slate-400" data-testid="ai-quota">
                {quota ? `−${quota.sip_ml} ml por creación · te quedan ${quota.remaining_ml} ml este mes` : ""}
              </span>
              <div className="flex gap-2">
                <button onClick={() => onClose?.()} className={BTN_GHOST} disabled={loading} data-testid="ai-cancel">Cancelar</button>
                <button onClick={generate} className={BTN_PRIMARY} disabled={loading} data-testid="ai-generate">
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} Generar
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      <CoffeeModal open={coffeeOpen} onClose={() => { setCoffeeOpen(false); refresh(); setGated(false); api.get("/ai/quota").then(({ data }) => setQuota(data)).catch(() => {}); }} />
    </div>
  );
}
